import {Injectable} from "@angular/core";
import {Actions, createEffect, CreateEffectMetadata, ofType} from '@ngrx/effects';
import * as dataSourcesUsage from "./dataSourcesUsage.actions";
import {Observable, switchMap, withLatestFrom} from 'rxjs';
import {filter, map, mergeMap} from 'rxjs/operators';
import {DataSetUsagesService} from "../../services/data-set-usages.service";
import {AdminService} from "../../services/admin.service";
import {showSnackbar} from "../snackbar/snackbar.actions";
import {TypedAction} from '@ngrx/store/src/models';
import {
  loadDataSetDimensions,
  loadDataSetDimensionsSuccess,
  updateDataSetDimension,
  updateDataSetDimensions,
  updateDataSetDimensionsSuccess,
  updateDataSetDimensionSuccess
} from '../dataSetDimensions/dataSetDimensions.actions';
import {
  initializeDataSourcesUsage,
  initializeDataSourcesUsageDimensionsSuccess,
  initializeDataSourcesUsageMetricsSuccess,
  loadDataSourcesUsage,
  loadDataSourcesUsageSuccess
} from './dataSourcesUsage.actions';
import {DimensionComponentObjects} from '../../classes/dimension-component-object';
import {AppState} from '../store';
import {Store} from '@ngrx/store';
import {selectDataSetDimensionsState} from '../dataSetDimensions/dataSetDimensions.selectors';
import {selectDataSetMetricsState} from '../dataSetMetrics/dataSetMetrics.selectors';
import {DataSetDimensionsState} from '../dataSetDimensions/dataSetDimensions';
import {DataSetMetricsState} from '../dataSetMetrics/dataSetMetrics';
import {
  loadDataSetMetrics,
  loadDataSetMetricsSuccess,
  updateDataSetMetric,
  updateDataSetMetrics,
  updateDataSetMetricsSuccess,
  updateDataSetMetricSuccess
} from '../dataSetMetrics/dataSetMetrics.actions';
import {MetricComponentObjects} from '../../classes/metric-component-object';
import {Serializer, SerializerResponse, Serializers} from "../../interfaces/serializer";
import {DataSource} from "../../interfaces/data-source";

@Injectable()
export class DataSourcesUsageEffects {
  public loadDataSourcesUsage$ = createEffect(() => this._actions$.pipe(
    ofType(dataSourcesUsage.loadDataSourcesUsage),
    switchMap(() => this._dataSetUsagesS.getDataSetUsages(false)
      .pipe(
        map((dataSources: SerializerResponse<Serializers<DataSource>>) => dataSourcesUsage.loadDataSourcesUsageSuccess({
          dataSources: dataSources.data
        }))
      ))
  ));

  public updateDataSourceUsage$ = createEffect(() => this._actions$.pipe(
    ofType(dataSourcesUsage.updateDataSourceUsage),
    mergeMap((action) => this._dataSetUsagesS.updateDataSource(action.dataSource.id, { enabled: action.update.enabled })
      .pipe(
        map((dataSource: SerializerResponse<Serializer<DataSource>>) => dataSourcesUsage.updateDataSourceUsageSuccess({
          dataSource: dataSource.data
        }))
      )
    )
  ));

  public manualImport$ = createEffect(() => this._actions$.pipe(
    ofType(dataSourcesUsage.launchImportDataSourcesUsage),
    mergeMap((action) => {
      const formData: any = {
        data_source_id:    action.dataSourceUsage.relationships.data_source.data.id,
        data_set_usage_id: action.dataSourceUsage.id,
        period:            action.allData && { type: 'custom', from: action.dataSourceUsage.attributes.import_range.from_date, to: action.dataSourceUsage.attributes.import_range.to_date } || action.period
      };

      if (action.dataSourceUsage.relationships.data_source.data.attributes.slug === 'adloop-tracking') {
        formData.only_attrib = action.onlyAttrib;
      }

      return this._adminS.launchManualImport(formData)
        .pipe(
          switchMap(() => [
              dataSourcesUsage.launchImportDataSourcesUsageSuccess(),
              showSnackbar({ message: 'data_sources.admin.import_in_progress' })
            ]
          )
        );
    })
  ));

  // ########## INIT ##########

  private readonly _init$: Observable<TypedAction<string>> & CreateEffectMetadata = createEffect((): Observable<TypedAction<string>> => this._actions$.pipe(
    ofType(
      loadDataSourcesUsage,
      loadDataSetDimensions,
      updateDataSetDimensions,
      updateDataSetDimension,
      loadDataSetMetrics,
      updateDataSetMetrics,
      updateDataSetMetric
    ),
    map((): TypedAction<string> => initializeDataSourcesUsage())
  ));

  private readonly _initDimensionsSuccess$: Observable<TypedAction<string> & { dimensions: DimensionComponentObjects }> = createEffect((): Observable<TypedAction<string> & { dimensions: DimensionComponentObjects }> => this._actions$.pipe(
    ofType(
      loadDataSourcesUsageSuccess,
      loadDataSetDimensionsSuccess,
      updateDataSetDimensionsSuccess,
      updateDataSetDimensionSuccess
    ),
    withLatestFrom(this._store.select(selectDataSetDimensionsState)),
    filter(([_, dataSetDimensionsState]: [TypedAction<string>, DataSetDimensionsState]): boolean => dataSetDimensionsState.loaded),
    map(([_, dataSetDimensionsState]: [TypedAction<string>, DataSetDimensionsState]): TypedAction<string> & { dimensions: DimensionComponentObjects } => initializeDataSourcesUsageDimensionsSuccess({
      dimensions: dataSetDimensionsState.componentObjects.source
    }))
  ));

  private readonly _initMetricsSuccess$: Observable<TypedAction<string> & { metrics: MetricComponentObjects }> = createEffect((): Observable<TypedAction<string> & { metrics: MetricComponentObjects }> => this._actions$.pipe(
    ofType(
      loadDataSourcesUsageSuccess,
      loadDataSetMetricsSuccess,
      updateDataSetMetricsSuccess,
      updateDataSetMetricSuccess
    ),
    withLatestFrom(this._store.select(selectDataSetMetricsState)),
    filter(([_, dataSetMetricsState]: [TypedAction<string>, DataSetMetricsState]): boolean => dataSetMetricsState.loaded),
    map(([_, dataSetMetricsState]: [TypedAction<string>, DataSetMetricsState]): TypedAction<string> & { metrics: MetricComponentObjects } => initializeDataSourcesUsageMetricsSuccess({
      metrics: dataSetMetricsState.componentObjects.source
    }))
  ));

  constructor(
    private readonly _actions$: Actions,
    private readonly _adminS: AdminService,
    private readonly _dataSetUsagesS: DataSetUsagesService,
    private readonly _store: Store<AppState>
  ) {}

}
